import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DeviceIds, deviceType } from '@/core/environment/deviceType';
import { useTracker } from '@/core/tracking';
import { usePageNameTracking } from '@/core/tracking/usePageNameTracking';
import { useTrackProcessedImageEvents } from '@/features/imageEditor/processedImageEvents/useTrackProcessedImageEvents';
import useUploadHandler from '@/features/uploadMedia/useUploadHandler';
import { ImageSelectorMedia } from '@/services/editorService';
import { useLocalization } from '@/services/localizationService';
import {
  BulkUploadTask,
  TaskStatus,
  TaskType,
} from '@/services/uploaderService';
import { useUser } from '@/services/userService';
import {
  bulkMediaListState,
  downloadMediaState,
  inputMediaNamesState,
  mediaInComparerIdChangeState,
  mediaShownInComparerState,
  mediaState,
} from '@/stores/mediaStore';
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';

import { Format, TaskToFormat } from '@/services/formatService/formatTypes';
import taskToFormatAssociationState from '@/stores/formatState';
import { onboardingEndedState } from '@/stores/onboardingStore';
import { AiPipelineChoices } from '@/services/settingsService/aiTypes';
import {
  useFormatService,
  useMediaService,
  usePaymentService,
} from '../appWrapper/ServiceProvider';

export const useResultLayout = () => {
  const router = useRouter();
  const [uploaderId, setUploaderId] = useState('result');

  const { track } = useTracker();

  const resetMedia = useResetRecoilState(mediaState);

  const { useMedia } = useMediaService();
  const { getMediaAndUpdateStore, getBulkUploadWithPolling } = useMedia();

  const [onboardingEnded, setOnboardingEnded] =
    useRecoilState(onboardingEndedState);
  const { formatFromMimeTypeAndPipeline } = useFormatService();

  usePageNameTracking('result');
  useTrackProcessedImageEvents();

  const { taskId, mediaType } = router.query;

  const [media, setMedia] = useRecoilState(mediaState);
  const [mediasToDownload, setMediasToDownload] =
    useRecoilState(downloadMediaState);
  const mediaInComparerIdChange = useRecoilValue(mediaInComparerIdChangeState);
  const resetMediaInComparerIdChange = useResetRecoilState(
    mediaInComparerIdChangeState
  );
  const [mediaInComparer, setMediaInComparer] = useRecoilState(
    mediaShownInComparerState
  );
  const [inputMediaNames, setInputMediaNames] =
    useRecoilState(inputMediaNamesState);
  const [bulkMediaList, setBulkMediaList] = useRecoilState(bulkMediaListState);

  const { data: user } = useUser();
  const { isPremiumUser, isPersonalUser, isBusinessUser } = usePaymentService();

  const { t, data } = useLocalization();

  const [bulkRawTaskList, setBulkRawTaskList] = useState<BulkUploadTask[]>([]);

  const setTaskToFormatAssociation = useSetRecoilState(
    taskToFormatAssociationState
  );

  const generateFormatFromOutput = useCallback(
    (aiPipeline: AiPipelineChoices, contentType: string): Format =>
      formatFromMimeTypeAndPipeline(contentType, aiPipeline),
    [formatFromMimeTypeAndPipeline]
  );

  const isFirstEnhancementInSession = useMemo(
    () => mediaInComparer.id === '',
    [mediaInComparer.id]
  );

  const isUserBusiness = isBusinessUser(user);

  // take the raw list of tasks in the bulk upload
  useEffect(() => {
    if (!media.taskList) {
      return;
    }
    const { taskList } = media;
    setBulkRawTaskList(taskList);
  }, [media]);

  // manipulate the raw list from the bulk upload to get the processed medias we need
  useEffect(() => {
    if (bulkRawTaskList.length === 0) {
      return;
    }

    const medias: ImageSelectorMedia[] = bulkRawTaskList.map((task, i) => {
      const outputToRender =
        task.result.outputs[0]?.convertedImages.length > 0
          ? task.result.outputs[0].convertedImages[0].outputUri
          : task.result.outputs[0]?.url;

      return task.result.outputs.length
        ? {
            id: task.taskId,
            outputUrl: outputToRender,
            inputUrl: task.result.inputUrl,
            convertedImages: task.result.outputs[0].convertedImages,
            contentType: task.result.outputs[0].contentType,
            hasWatermark: task.result.outputs[0].hasWatermark,
            aiPipeline: task.result.outputs[0].aiPipeline,
            bulkPosition: i,
            status: task.status,
          }
        : {
            id: task.taskId,
            outputUrl: '',
            inputUrl: task.result.inputUrl,
            bulkPosition: i,
            status: task.status,
          };
    });
    setBulkMediaList(medias);
  }, [bulkRawTaskList, setBulkMediaList]);

  // store the output format for all medias in the bulk upload response
  useEffect(() => {
    if (bulkMediaList.length === 0) {
      return;
    }

    const taskToFormat: TaskToFormat = {};

    bulkMediaList.forEach((bulkMedia) => {
      if (bulkMedia.status === TaskStatus.Processing) {
        return;
      }

      const outputFormat = generateFormatFromOutput(
        bulkMedia.aiPipeline!,
        bulkMedia.contentType!
      );
      taskToFormat[bulkMedia.id!] = outputFormat;
    });

    setTaskToFormatAssociation((prev) => ({ ...taskToFormat, ...prev }));
  }, [bulkMediaList, generateFormatFromOutput, setTaskToFormatAssociation]);

  // provide a placeholder for media file names if page is reloaded
  useEffect(() => {
    if (bulkRawTaskList.length === 0 || inputMediaNames.length > 0) {
      return;
    }

    const mediaNames = Array(bulkRawTaskList.length).fill(
      'image.remini-enhanced',
      0
    );

    setInputMediaNames(mediaNames);
  }, [bulkRawTaskList.length, inputMediaNames.length, setInputMediaNames]);

  const getDefaultMediaToDownloadWhenNotBulk = useCallback(
    (): ImageSelectorMedia => ({
      id: media.id!,
      outputUrl: media.outputUrl!,
      inputUrl: media.inputUrl,
      convertedImages: media.convertedImages,
      contentType: media.outputContentType,
      hasWatermark: media.hasWatermark,
      aiPipeline: media.aiPipeline,
    }),
    [media]
  );

  // set download medias state in case of videos
  useEffect(() => {
    if (media.taskList) {
      return;
    }

    const defaultMediaToDownload = getDefaultMediaToDownloadWhenNotBulk();

    const hasOutputUrlChanged =
      mediaInComparer.outputUrl.split('?')[0] !==
      defaultMediaToDownload.outputUrl.split('?')[0];

    if (isFirstEnhancementInSession || hasOutputUrlChanged) {
      setMediaInComparer(defaultMediaToDownload);
      setMediasToDownload([defaultMediaToDownload]);
    }
  }, [
    getDefaultMediaToDownloadWhenNotBulk,
    isFirstEnhancementInSession,
    media.taskList,
    mediaInComparer.outputUrl,
    setMediaInComparer,
    setMediasToDownload,
  ]);

  // change media in comparer after change of ID / status / watermark presence or reprocess
  useEffect(() => {
    if (bulkMediaList.length === 0 || !media.taskList) {
      return;
    }

    let remoteMediaInComparer = bulkMediaList.find(
      (mediaInList) => mediaInList.id === mediaInComparer.id
    );

    if (!remoteMediaInComparer) {
      if (
        mediaInComparerIdChange.oldTaskId !== mediaInComparerIdChange.taskId
      ) {
        remoteMediaInComparer = bulkMediaList.find(
          (mediaInList) => mediaInList.id === mediaInComparerIdChange.taskId
        );
        resetMediaInComparerIdChange();
      } else {
        [remoteMediaInComparer] = bulkMediaList;
      }
    }

    // typescript doesn't realize that remoteMediaInComparer is always defined
    if (
      isFirstEnhancementInSession ||
      (remoteMediaInComparer!.id === mediaInComparer.id &&
        remoteMediaInComparer!.status === mediaInComparer.status &&
        remoteMediaInComparer!.hasWatermark === mediaInComparer.hasWatermark)
    ) {
      return;
    }

    setMediaInComparer(remoteMediaInComparer!);
  }, [
    bulkMediaList,
    isFirstEnhancementInSession,
    media.taskList,
    mediaInComparer.hasWatermark,
    mediaInComparer.id,
    mediaInComparer.status,
    mediaInComparerIdChange.oldTaskId,
    mediaInComparerIdChange.taskId,
    resetMediaInComparerIdChange,
    setMediaInComparer,
  ]);

  const getIndexOfFirstCompletedTask = useCallback((): number => {
    const firstCompletedTaskIndex = bulkMediaList.findIndex(
      (m) =>
        m.status === TaskStatus.Completed || m.status === TaskStatus.Exported
    );

    return firstCompletedTaskIndex >= 0 ? firstCompletedTaskIndex : 0;
  }, [bulkMediaList]);

  const getLastSelectedMediasUpdated = useCallback((): ImageSelectorMedia[] => {
    if (bulkMediaList.length === 1) {
      return bulkMediaList;
    }

    const currentSelectedMediaIds = mediasToDownload.map((m) => m.id);
    return bulkMediaList.filter((m) => currentSelectedMediaIds.includes(m.id));
  }, [bulkMediaList, mediasToDownload]);

  // set download medias state in case of images (bulk upload)
  useEffect(() => {
    if (!media.taskList) {
      return;
    }

    if (isFirstEnhancementInSession) {
      if (bulkMediaList.length === 0) {
        return;
      }

      const mediaInComparerIndex = getIndexOfFirstCompletedTask();
      const mediaInComparer = bulkMediaList[mediaInComparerIndex];

      setMediasToDownload([mediaInComparer]);
      setMediaInComparer(mediaInComparer);

      return;
    }

    const newMediasToDownload = getLastSelectedMediasUpdated();

    // get status to account for exports (same ID, different status) or bulk position for deletions
    const currentSelectedMediasDescriptiveList = mediasToDownload.map(
      (m) => `${m.id}-${m.status}-${m.bulkPosition}-${m.hasWatermark}`
    );
    const newSelectedMediasDescriptiveList = newMediasToDownload.map(
      (m) => `${m.id}-${m.status}-${m.bulkPosition}-${m.hasWatermark}`
    );

    const hasSelectedMediaListChanged =
      newMediasToDownload.length !== mediasToDownload.length ||
      newSelectedMediasDescriptiveList.some(
        (mediaDescription) =>
          !currentSelectedMediasDescriptiveList.includes(mediaDescription)
      );

    if (hasSelectedMediaListChanged) {
      setMediasToDownload(newMediasToDownload);
    }
  }, [
    bulkMediaList,
    getIndexOfFirstCompletedTask,
    getLastSelectedMediasUpdated,
    isFirstEnhancementInSession,
    media.taskList,
    mediasToDownload,
    setMediaInComparer,
    setMediasToDownload,
  ]);

  useEffect(() => {
    if (mediasToDownload.length === 0) {
      setMediasToDownload([mediaInComparer]);
    }
  }, [mediasToDownload.length, mediaInComparer, setMediasToDownload]);

  useEffect(() => {
    const pollingBulkUpload = async () => {
      await getBulkUploadWithPolling({
        isBulkProcessingCompleted: true,
      });
    };

    if (mediaType === TaskType.Image && taskId) pollingBulkUpload();

    return () => {
      pollingBulkUpload();
    };
  }, [getBulkUploadWithPolling, media.mediaType]);

  // isUserBusiness is listed as dependency to trigger data refetch when user change its business status
  useEffect(() => {
    if (taskId) {
      getMediaAndUpdateStore();
    }
  }, [getMediaAndUpdateStore, setMedia, taskId, track, isUserBusiness]);

  useEffect(() => resetMedia, [resetMedia]);

  useEffect(() => {
    if (media.id) {
      setUploaderId(`result-${media.id}`);
    }
  }, [media]);

  const isDesktop = useMemo(() => deviceType() === DeviceIds.Desktop, []);

  const { reprocess } = useUploadHandler('result');

  useEffect(() => {
    if (onboardingEnded && !!user && isPremiumUser(user)) {
      setOnboardingEnded(false);
    }
  }, [
    onboardingEnded,
    reprocess,
    router,
    taskId,
    setOnboardingEnded,
    user,
    isPremiumUser,
  ]);

  const onUpload = useCallback(
    (taskId: string) =>
      taskId &&
      router.replace(
        {
          query: { ...router.query, taskId },
        },
        undefined,
        { shallow: true }
      ),
    [router]
  );

  const shouldShowEnhancementsLeftBadge = useMemo(
    () =>
      (!!user &&
        isPersonalUser(user) &&
        !user.monetization.hasIncompleteSubscription) ||
      (!!user && user.availableCredits! > 0),
    [isPersonalUser, user]
  );

  const enhancementsLeftText = useMemo(
    () =>
      t(data.result.photosLeft, {
        n: user?.imageDownloadsAvailable || user?.availableCredits,
      }),
    [
      data.result.photosLeft,
      t,
      user?.imageDownloadsAvailable,
      user?.availableCredits,
    ]
  );

  return {
    uploaderId,
    media,
    setMedia,
    isVideo: media.mediaType === TaskType.Video,
    onUpload,
    isMediaReady: !!media.outputUrl || !!media.bulkUploadId,
    isMediaVisible: !media.isUnavailable,
    isImageSelectorToolbarVisible:
      isDesktop && media.mediaType === TaskType.Image,
    isImageEditorVisible:
      isDesktop && !media.isUnavailable && media.mediaType === TaskType.Image,
    shouldShowEnhancementsLeftBadge,
    enhancementsLeftText,
  };
};
